import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { GET_PRODUCT_MIX_SLOT } from '../../graphql/reports-queries'
import { formatEuros, dateRangeToUtc } from '../../lib/format'
import { DAY_LABELS, downloadReportCsv, SectionTitle, EmptyData, TableLoading, QueryError } from './shared'
import type { SlotSelection } from './shared'

// ─── Types ────────────────────────────────────────────────────────────────────

type MixView = 'ORDERED' | 'PRODUCED'

interface ProductSalesStats {
  /**
   * revenueCents is null on every topProducts row in PRODUCED view, and null
   * everywhere without order.financial.view (API gated both since 2026-08-30 —
   * UpgradeStat.revenueCents is nullable now too).
   */
  topProducts: { productName: string; variantName: string; qtySold: number; revenueCents: number | null }[]
  topUpgrades: { variantName: string; count: number; revenueCents: number | null }[]
  customizationFrequency: {
    groupName: string
    totalCount: number
    options: { label: string; count: number; priceDeltaCents: number }[]
  }[]
}

const RANK_COLORS = ['#CA4330', '#853F82', '#DC9C4E']
const getRankColor = (i: number) => RANK_COLORS[i] ?? '#9CA3AF'

const VIEW_CAPTIONS: Record<MixView, string> = {
  ORDERED: 'What customers ordered (meal deals as one line)',
  PRODUCED: 'What physically left the kitchen (meal-deal components as units)',
}

// ─── Panel ────────────────────────────────────────────────────────────────────

const ProductMixPanel = ({
  selection,
  startDate,
  endDate,
  onClose,
}: {
  selection: SlotSelection
  startDate: string
  endDate: string
  onClose: () => void
}) => {
  const [view, setView] = useState<MixView>('ORDERED')
  const [downloading, setDownloading] = useState(false)

  const slot =
    selection.kind === 'hour'
      ? { iso_days: [selection.iso_day], hours: [selection.hour] }
      : { iso_days: [selection.iso_day], dayparts: [selection.daypart] }

  const { data, loading, error } = useQuery<{ productSalesStats: ProductSalesStats }>(
    GET_PRODUCT_MIX_SLOT,
    {
      variables: { filters: dateRangeToUtc(startDate, endDate), slot, view },
      fetchPolicy: 'cache-and-network',
    }
  )

  const stats = data?.productSalesStats
  const topProducts = stats?.topProducts ?? []
  const topUpgrades = stats?.topUpgrades ?? []
  const customizations = stats?.customizationFrequency ?? []

  // PRODUCED hides money entirely — half a revenue picture reads as a bug.
  const showRevenue = topProducts.some((p) => p.revenueCents != null)
  const maxQty = topProducts.reduce((max, p) => (p.qtySold > max ? p.qtySold : max), 0)

  const slotLabel =
    selection.kind === 'hour' ? `${selection.hour}:00–${selection.hour + 1}:00` : selection.label

  const handleExport = async () => {
    let path = `/api/reports/product-mix?start_date=${startDate}&end_date=${endDate}&view=${view.toLowerCase()}`
    path +=
      selection.kind === 'hour'
        ? `&iso_days=${selection.iso_day}&hours=${selection.hour}`
        : `&iso_days=${selection.iso_day}&dayparts=${selection.daypart}`

    setDownloading(true)
    try {
      await downloadReportCsv(path, `product-mix-${startDate}-${endDate}.csv`)
    } catch {
      toast.error('Export failed. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  const isEmpty =
    stats != null && topProducts.length === 0 && topUpgrades.length === 0 && customizations.length === 0

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-gray-600 bg-opacity-50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-xl h-full overflow-y-auto flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {DAY_LABELS[selection.iso_day - 1]} · {slotLabel}
            </h2>
            <p className="text-xs text-gray-400">
              {startDate} – {endDate}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-5 space-y-6 flex-1">
          <div>
            <div className="inline-flex items-center gap-1 bg-gray-100 rounded-xl p-1">
              {(['ORDERED', 'PRODUCED'] as MixView[]).map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    view === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {v === 'ORDERED' ? 'Ordered' : 'Produced'}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2">{VIEW_CAPTIONS[view]}</p>
          </div>

          {loading && !stats && <TableLoading />}
          {error && <QueryError message={`Failed to load product mix: ${error.message}`} />}
          {isEmpty && <EmptyData />}

          {topProducts.length > 0 && (
            <div>
              <SectionTitle>Top products</SectionTitle>
              <div className="space-y-3">
                {topProducts.map((product, i) => {
                  const rankColor = getRankColor(i)
                  const barPct = maxQty > 0 ? (product.qtySold / maxQty) * 100 : 0
                  return (
                    <div key={`${product.productName}|${product.variantName}`} className="flex items-center gap-3">
                      <span
                        className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: rankColor }}
                      >
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-800 truncate pr-4">
                            {product.productName}
                            {product.variantName && product.variantName !== product.productName && (
                              <span className="text-gray-400"> · {product.variantName}</span>
                            )}
                          </span>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-sm font-bold text-gray-900">{product.qtySold}×</span>
                            {showRevenue && product.revenueCents != null && (
                              <span className="text-xs text-gray-400 w-16 text-right">
                                {formatEuros(product.revenueCents)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${barPct}%`, backgroundColor: rankColor }}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {topUpgrades.length > 0 && (
            <div>
              <SectionTitle>Bundle upgrades</SectionTitle>
              <div className="space-y-2">
                {topUpgrades.map((upgrade) => (
                  <div key={upgrade.variantName} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 truncate pr-4">{upgrade.variantName}</span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="font-semibold text-gray-900">{upgrade.count}×</span>
                      {showRevenue && upgrade.revenueCents != null && (
                        <span className="text-xs text-gray-400 w-16 text-right">
                          {formatEuros(upgrade.revenueCents)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {customizations.length > 0 && (
            <div>
              <SectionTitle>Customizations</SectionTitle>
              <div className="space-y-4">
                {customizations.map((group) => (
                  <div key={group.groupName}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-800">{group.groupName}</span>
                      <span className="text-gray-400">{group.totalCount}×</span>
                    </div>
                    <div className="mt-1 space-y-1">
                      {group.options.map((option) => (
                        <div
                          key={option.label}
                          className="flex items-center justify-between text-xs text-gray-500"
                        >
                          <span className="truncate pr-4">{option.label}</span>
                          <span className="flex-shrink-0">{option.count}×</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-3">
          <button
            onClick={handleExport}
            disabled={downloading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors whitespace-nowrap"
          >
            <ArrowDownTrayIcon className={`h-4 w-4 ${downloading ? 'animate-bounce' : ''}`} />
            {downloading ? 'Downloading…' : 'Export CSV'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ProductMixPanel
